import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, Play, Square, Volume2, VolumeX } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/ui/components/ui/button";
import { Badge } from "@/ui/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/components/ui/popover";
import { ALERTS_URL } from "@/alerts/config/alerts-urls";
import { useAlertLiveStore, type AlertLiveStatus } from "../stores/use-alert-live-store";
import { useAlertNotifyStore } from "../stores/use-alert-notify-store";
import { prepareAlertSound, stopAlertSound, toggleAlarmPreview } from "../utils/play-alert-sound";
import { ALARM_SOUNDS, ALARM_PATTERN_MS, getAlarmSound } from "../utils/alarm-sounds";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/ui/select";

const STATUS_KEY: Record<
  AlertLiveStatus,
  | "liveStatus.idle"
  | "liveStatus.subscribing"
  | "liveStatus.connecting"
  | "liveStatus.live"
  | "liveStatus.polling"
  | "liveStatus.degraded"
> = {
  idle: "liveStatus.idle",
  subscribing: "liveStatus.subscribing",
  connecting: "liveStatus.connecting",
  live: "liveStatus.live",
  polling: "liveStatus.polling",
  degraded: "liveStatus.degraded",
};

/**
 * Global shortcut for the current queue workload. Sound is intentionally not
 * produced here — whether a new alert may sound is decided in
 * `use-alert-notify-feed.ts` against MIN-223's policy.
 */
export function AlertsBell() {
  const { t } = useTranslation("alerts");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // Unresolved NEW alerts — the same set the alarm rings for, so the badge and
  // the sound can never disagree. Distinct from `queueCount` below: this is what
  // arrived and still needs the operator, that is the whole standing workload.
  const unresolvedCount = useAlertNotifyStore((state) => state.newIds.length);
  // Server total for the scope, from GET /api/alerts/queue/ — never derived from
  // the loaded page, which is one window onto a much longer queue.
  const queueCount = useAlertLiveStore((state) => state.queueCount);
  const status = useAlertLiveStore((state) => state.status);
  const lastReconciledAt = useAlertLiveStore((state) => state.lastReconciledAt);
  const muted = useAlertNotifyStore((state) => state.muted);
  const setMuted = useAlertNotifyStore((state) => state.setMuted);
  const alarmSoundId = useAlertNotifyStore((state) => state.alarmSoundId);
  const setAlarmSoundId = useAlertNotifyStore((state) => state.setAlarmSoundId);
  const selectedSound = getAlarmSound(alarmSoundId);
  // Mirrors whether a preview is sounding, so the control can offer Stop. Cleared
  // by a timer when the pattern ends on its own, and on unmount/mute.
  const [previewing, setPreviewing] = useState(false);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const endPreview = useCallback(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = null;
    setPreviewing(false);
  }, []);

  const startPreview = useCallback(
    (id: string) => {
      // Toggle: a second click on a running preview stops it.
      const started = toggleAlarmPreview(id);
      endPreview();
      if (!started) return;
      setPreviewing(true);
      previewTimer.current = setTimeout(() => setPreviewing(false), ALARM_PATTERN_MS);
    },
    [endPreview],
  );

  // Never leave a preview sounding after the popover closes, the sound is muted,
  // or the component unmounts.
  useEffect(() => {
    if (!open || muted) {
      stopAlertSound();
      endPreview();
    }
  }, [open, muted, endPreview]);
  useEffect(
    () => () => {
      stopAlertSound();
      if (previewTimer.current) clearTimeout(previewTimer.current);
    },
    [],
  );
  const count = unresolvedCount;

  const openQueue = () => {
    setOpen(false);
    navigate(ALERTS_URL.QUEUE);
  };

  const toggleSound = () => {
    const nextMuted = !muted;
    setMuted(nextMuted);
    if (!nextMuted) prepareAlertSound();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9"
          aria-label={t("bell.trigger", { n: count })}
        >
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center px-1 text-xs"
            >
              {count > 99 ? "99+" : count}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-72 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">{t("bell.title")}</span>
          <button
            type="button"
            onClick={toggleSound}
            title={muted ? t("bell.unmute") : t("bell.mute")}
            aria-label={muted ? t("bell.unmute") : t("bell.mute")}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>

        <div className="space-y-3 p-3">
          <div>
            <div className="font-mono text-2xl font-semibold tabular-nums">
              {unresolvedCount}
            </div>
            <div className="text-xs text-muted-foreground">
              {t("bell.unresolved", { count: unresolvedCount })}
            </div>
            {queueCount != null && (
              <div className="mt-1 text-xs text-muted-foreground">
                {t("bell.queuePending", { n: queueCount })}
              </div>
            )}
          </div>
          <div className="rounded-md border bg-muted/30 px-2.5 py-2 text-xs">
            <div className="font-medium">{t(STATUS_KEY[status])}</div>
            <div className="mt-0.5 text-muted-foreground">
              {lastReconciledAt ? t("bell.upToDate") : t("bell.waitingForData")}
            </div>
          </div>
          {/* Alarm sound. The new-alert alarm repeats until the operator opens
              the alert, so which pattern plays is an operator preference —
              quiet control room vs noisy plant. Persisted. */}
          <div className="space-y-1.5 border-t pt-3">
            <div className="flex items-center justify-between">
              <label htmlFor="alarm-sound" className="text-xs font-medium">
                {t("bell.alarmSound")}
              </label>
              <button
                type="button"
                onClick={() => startPreview(alarmSoundId)}
                disabled={muted}
                title={
                  muted
                    ? t("bell.previewMuted")
                    : previewing
                      ? t("bell.previewStop")
                      : t("bell.previewPlay")
                }
                className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
              >
                {previewing ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                {previewing ? t("bell.stop") : t("bell.preview")}
              </button>
            </div>
            <Select
              value={alarmSoundId}
              onValueChange={(id) => {
                setAlarmSoundId(id);
                // Audition the pick immediately — choosing an alarm you can't
                // hear is the whole problem this control solves. Stop first so a
                // running preview never overlaps the new one.
                stopAlertSound();
                endPreview();
                if (!muted) startPreview(id);
              }}
            >
              <SelectTrigger id="alarm-sound" className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ALARM_SOUNDS.map((sound) => (
                  <SelectItem key={sound.id} value={sound.id} className="text-xs">
                    {t(sound.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              {muted
                ? t("bell.muted")
                : t("bell.loops", { hint: t(selectedSound.hintKey) })}
            </p>
          </div>

          <Button className="w-full" size="sm" onClick={openQueue}>
            {t("bell.openQueue")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
